import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../services/firebase';
import { 
  onAuthStateChanged, 
  signInAnonymously, 
  signOut as firebaseSignOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  updateProfile,
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink
} from 'firebase/auth';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [magicLinkEmail, setMagicLinkEmail] = useState('');

  // Email/Password Registration
  async function signUp(email, password, displayName) {
    try {
      setError('');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update profile with display name
      if (displayName) {
        await updateProfile(userCredential.user, {
          displayName: displayName
        });
      }
      
      return userCredential.user;
    } catch (error) {
      console.error('Sign up error:', error);
      setError(error.message || 'Error during registration');
      throw error;
    }
  }

  // Email/Password Sign In
  async function signIn(email, password) {
    try {
      setError('');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      console.error('Sign in error:', error);
      setError(error.message || 'Error during sign in');
      throw error;
    }
  }


  // Google Sign In
  async function signInWithGoogle() {
    try {
      setError('');
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      return userCredential.user;
    } catch (error) {
      console.error("Google Sign In Error:", error);
      setError(error.message || 'Failed to sign in with Google');
      throw error;
    }
  }

  // Apple Sign In
  async function signInWithApple() {
    try {
      setError('');
      const provider = new OAuthProvider('apple.com');
      provider.addScope('email');
      provider.addScope('name');
      const userCredential = await signInWithPopup(auth, provider);
      return userCredential.user;
    } catch (error) {
      console.error("Apple Sign In Error:", error);
      setError(error.message || 'Failed to sign in with Apple');
      throw error;
    }
  }
  
  function signInAnon() {
    setError('');
    return signInAnonymously(auth);
  }

  function signOut() {
    setError('');
    return firebaseSignOut(auth);
  }

  // Nuclear option - completely wipe authentication state
  async function forceSignOut() {
    try {
      setError('');
      console.log('🧹 Force signing out and clearing all auth data...');
      
      // Sign out from Firebase
      await firebaseSignOut(auth);
      
      // Clear all stored authentication data
      localStorage.clear();
      sessionStorage.clear();
      
      // Reset component state
      setCurrentUser(null);
      setMagicLinkEmail('');
      setLoading(false);
      
      console.log('✅ Force sign out complete');
      return true;
    } catch (error) {
      console.error('❌ Force sign out error:', error);
      // Even if Firebase signout fails, clear local state
      localStorage.clear();
      sessionStorage.clear();
      setCurrentUser(null);
      setMagicLinkEmail('');
      setLoading(false);
      return false;
    }
  }

  // Password Reset
  async function resetPassword(email) {
    try {
      setError('');
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      console.error('Password reset error:', error);
      
      let errorMessage = 'Error sending password reset email';
      if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address';
      }
      
      setError(errorMessage);
      throw error;
    }
  }

  // Send Magic Link
  async function sendMagicLink(email) {
    try {
      setError('');
      console.log('🚀 Sending magic link to:', email);
      
      const currentUrl = window.location.origin;
      console.log('📍 Current URL origin:', currentUrl);
      
      const actionCodeSettings = {
        url: currentUrl,
        handleCodeInApp: true,
        iOS: {
          bundleId: 'com.flyp.autofill'
        },
        android: {
          packageName: 'com.flyp.autofill',
          installApp: true,
          minimumVersion: '12'
        },
        dynamicLinkDomain: undefined // Let Firebase use default
      };
      
      console.log('⚙️ Action code settings:', actionCodeSettings);
      
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      
      // Store email for verification
      localStorage.setItem('magicLinkEmail', email);
      setMagicLinkEmail(email);
      
      console.log('✅ Magic link sent successfully');
      return true;
    } catch (error) {
      console.error('❌ Magic link error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      let errorMessage = 'Failed to send magic link';
      if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/unauthorized-continue-uri') {
        errorMessage = 'Domain not authorized in Firebase Console. Please add your domain to authorized domains.';
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'Email link authentication is not enabled in Firebase Console.';
      } else if (error.code === 'auth/argument-error') {
        errorMessage = 'Invalid configuration. Please check Firebase setup.';
      } else if (error.code === 'auth/internal-error') {
        errorMessage = 'Firebase internal error. Please try again.';
      }
      
      setError(errorMessage);
      throw error;
    }
  }

  // Verify Magic Link
  async function verifyMagicLink(email, url) {
    try {
      setError('');
      console.log('🔍 Verifying magic link for email:', email);
      console.log('🔗 Magic link URL:', url);
      
      if (!isSignInWithEmailLink(auth, url)) {
        console.log('❌ URL is not a valid sign-in link');
        throw new Error('Invalid magic link');
      }
      
      console.log('✅ URL is valid sign-in link, attempting sign-in...');
      const result = await signInWithEmailLink(auth, email, url);
      
      // Clear stored email
      localStorage.removeItem('magicLinkEmail');
      setMagicLinkEmail('');
      
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      console.log('🎉 Magic link sign-in successful!', result.user);
      return result.user;
    } catch (error) {
      console.error('❌ Magic link verification error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      let errorMessage = 'Invalid or expired magic link';
      if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/invalid-action-code') {
        errorMessage = 'This magic link has expired or been used already';
      } else if (error.code === 'auth/expired-action-code') {
        errorMessage = 'This magic link has expired. Please request a new one.';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled';
      }
      
      setError(errorMessage);
      throw error;
    }
  }


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('🔄 Auth state changed:', user ? `User: ${user.email || user.uid}` : 'No user');
      
      // Check if this is a transition from anonymous to real user
      const wasAnonymous = currentUser?.isAnonymous;
      const isNowRealUser = user && !user.isAnonymous;
      
      if (wasAnonymous && isNowRealUser) {
        console.log('🔄 User upgraded from anonymous to real - updating state instead of reloading');
        // Instead of reloading, just update the state normally
        setCurrentUser(user);
        setLoading(false);
        return;
      }
      
      setCurrentUser(user);
      setLoading(false);
      console.log('✅ Auth context loading set to false');
    });

    return unsubscribe;
  }, [currentUser]);

  // Check for magic link on page load
  useEffect(() => {
    const handleMagicLink = async () => {
      const url = window.location.href;
      console.log('🔍 Checking URL for magic link:', url);
      
      if (isSignInWithEmailLink(auth, url)) {
        console.log('✅ Magic link detected, preventing anonymous sign-in');
        // Get stored email
        let email = localStorage.getItem('magicLinkEmail');
        
        if (!email) {
          // Prompt for email if not found (cross-device)
          email = window.prompt('Please enter your email address to complete sign in:');
        }
        
        if (email) {
          try {
            // Add timeout to prevent hanging
            const timeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Magic link verification timeout')), 30000)
            );
            
            await Promise.race([
              verifyMagicLink(email, url),
              timeoutPromise
            ]);
            console.log('🎉 Magic link verification completed successfully');
          } catch (error) {
            console.error('❌ Magic link verification failed:', error);
            if (error.message === 'Magic link verification timeout') {
              setError('Sign-in is taking too long. Please try again.');
            }
            // Clear bad URL
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        }
      } else {
        console.log('ℹ️ No magic link found in URL');
      }
    };

    handleMagicLink();
  }, []);

  // Initialize stored email on load
  useEffect(() => {
    const storedEmail = localStorage.getItem('magicLinkEmail');
    if (storedEmail) {
      setMagicLinkEmail(storedEmail);
    }
  }, []);

  // Skip automatic anonymous sign-in since it's disabled in Firebase
  useEffect(() => {
    const url = window.location.href;
    const hasMagicLink = isSignInWithEmailLink(auth, url);
    const hasPendingEmail = localStorage.getItem('magicLinkEmail');
    
    console.log('🤔 Auth check (anonymous disabled):', {
      loading,
      currentUser: currentUser?.email || currentUser?.uid || 'none',
      isAnonymous: currentUser?.isAnonymous,
      hasMagicLink,
      hasPendingEmail: !!hasPendingEmail
    });
    
    // Don't attempt anonymous sign-in since it's disabled
    // Just let the user manually sign in with other methods
  }, [loading, currentUser]);

  const value = {
    currentUser,
    signUp,
    signIn,
    signInWithGoogle,
    signInWithApple,
    signInAnon,
    signOut,
    forceSignOut,
    resetPassword,
    sendMagicLink,
    verifyMagicLink,
    magicLinkEmail,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}